/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { InputField } from "../../atoms/InputField";
import { responsiveStyles } from "../../commons/styles";
import { RegistrationFooterButton } from "../../components/RegistrationFooterButton";
import { RegistrationLayout } from "../../components/RegistrationLayout";
import {
  ButtonClickType,
  inputFieldMandatoryLabel,
  inputFieldOptionalLabel,
  regexValidation,
} from "../../constants/commons";
import { useIsMobile } from "../../hooks/useIsMobile";
import { BankAccountDetailsProps } from "./interfaces";
import { bankAccountDetailsStyle } from "./styles";
import errorLogo from "../../assets/error-icon.svg";
import useSTR from "react-str";
import { getData, postData } from "../../utils/fetchers";
import { ShowMessage } from "../../components/ShowMessage";
import {
  getErrorForField,
  getFieldConfigStatus,
  isFieldOptional,
  showDisplayHeadingAndContentFromLoadingStatus,
} from "../../utils/helpers";
import { URL } from "../../API/urlEndpoints";
import * as _ from "lodash";
import { useUserContext } from "../../hooks/cookieHooks";
import { toast } from "../../utils/toast";
import { ErrorComponent } from "../../molecules/ErrorComponent";
import {
  FieldRenderState,
  RegisterationFields,
} from "../../commons/interfaces";

export const BankAccountDetails: React.FunctionComponent<BankAccountDetailsProps> = (
  props
) => {
  const {
    onBackButtonClick,
    onSaveAndExitClick,
    SuccessFullSaveObj,
    pageNavigationDetails,
    onNextClick,
  } = props;
  const { nextScreen } = pageNavigationDetails;
  const isMobileView = useIsMobile();
  // State to maintain the Bank Account Holder Name
  const [bankAccHolderName, setBankAccHolderName] = React.useState<string>();
  // State to maintain the Bank Account Number
  const [bankAccNumber, setBankAccNumber] = React.useState<string>();
  // State to maintain the IFSC Number
  const [ifscCode, setIfscCode] = React.useState<string>();

  // State which shows the validity of the entered Bank Account Number
  const [
    isEneteredBankAccountNumberValid,
    setIsEneteredBankAccountNumberValid,
  ] = React.useState<boolean>();
  // State which shows the validity of the entered IFSC Number
  const [
    isEneteredIFSCValid,
    setIsEneteredIFSCValid,
  ] = React.useState<boolean>();
  // State checking whether user can be navigated to the next page or not
  const [
    isNextPageNavAllowed,
    setIsNextPageNavAllowed,
  ] = React.useState<boolean>(false);
  // Calling the SSO Hook to retrieve user information of the logged in user
  const user = useUserContext();

  // [GET] On page load
  const [
    ,
    {
      result: getBankAccountDetails,
      revalidator: refetchBankAccountDetails,
      loading: loadingBankAccountDetails,
      error: errorBankAccountDetails,
    },
  ] = useSTR(getData);

  // [POST] For Saving data on page.
  const [
    ,
    {
      result: postBankAccountDetails,
      revalidator: refetchPostBankAccountDetails,
      loading: loadingPostBankAccountDetails,
      error: errortPostBankAccountDetails,
    },
  ] = useSTR(postData);

  // On Page load, calling the GET API call
  React.useEffect(() => {
    refetchBankAccountDetails(
      `${URL.getBankAccountDetails(_.get(user, "empId"))}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Setting respective state variables once data is fetched
  React.useEffect(() => {
    if (getBankAccountDetails) {
      const {
        bankAccountNumber,
        ifscCode,
        bankAccounHolderName,
      } = getBankAccountDetails;
      if (bankAccounHolderName) {
        setBankAccHolderName(bankAccounHolderName);
      }
      if (bankAccountNumber) {
        setBankAccNumber(bankAccountNumber);
        setIsEneteredBankAccountNumberValid(true);
      }
      if (ifscCode) {
        setIfscCode(ifscCode);
        setIsEneteredIFSCValid(true);
      }
    }
  }, [getBankAccountDetails]);

  // Function which is called when the Bank Acc Holder Name is changed
  const onBankAccHolderNameChange = (enteredBankAccHolderName: string) => {
    setBankAccHolderName(enteredBankAccHolderName);
  };

  // Function which is called when the Bank Acc Number is changed
  const onBankAccNumberChange = (enteredBankAccNumber: string) => {
    setBankAccNumber(enteredBankAccNumber);
  };

  // Function which is called when the Bank Acc Number is changed
  const onIFSCNumberChange = (IFSCNumber: string) => {
    setIfscCode(IFSCNumber);
  };

  // Function which allows the user to go to the next screen
  const isSubmitAllowed = React.useMemo(() => {
    return !(
      bankAccHolderName &&
      bankAccNumber &&
      ifscCode &&
      isEneteredBankAccountNumberValid &&
      isEneteredIFSCValid
    );
  }, [
    bankAccHolderName,
    bankAccNumber,
    ifscCode,
    isEneteredBankAccountNumberValid,
    isEneteredIFSCValid,
  ]);

  // Function which constructs the necessary JSON as per the API call
  const constructDataForPOSTAPI = React.useCallback(() => {
    let bankAccountDetailsObj = {
      bankAccounHolderName: bankAccHolderName,
      bankAccountNumber: bankAccNumber,
      ifscCode: ifscCode,
    };
    return bankAccountDetailsObj;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccHolderName, bankAccNumber, ifscCode]);

  // Callback from the parent Component to SAVE AND QUIT
  React.useEffect(() => {
    onSaveAndExitClick && onPositiveActionClick(ButtonClickType.SAVE_AND_EXIT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveAndExitClick]);

  // Function which decides whether the control should be re-directed to the next sreen or just SAVE
  const onPositiveActionClick = (buttonClickType: ButtonClickType) => {
    const bankaccountDetailsObj = constructDataForPOSTAPI();

    switch (buttonClickType) {
      case ButtonClickType.NEXT:
        let paramsObj = {
          ...bankaccountDetailsObj,
          customHeader: { change_state: true },
        };
        // POST CALL
        refetchPostBankAccountDetails(
          `${URL.postBankAccountDetails(_.get(user, "empId"))}`,
          paramsObj
        );
        setIsNextPageNavAllowed(true);
        break;

      case ButtonClickType.SAVE_AND_EXIT:
        // POST CALL
        refetchPostBankAccountDetails(
          `${URL.postBankAccountDetails(_.get(user, "empId"))}`,
          bankaccountDetailsObj
        );
        setIsNextPageNavAllowed(false);
        break;

      default:
        break;
    }
  };

  // Showing the success message in a toast component / Fetching data From Server
  React.useEffect(() => {
    if (postBankAccountDetails) {
      postBankAccountDetails?.message &&
        toast.success(postBankAccountDetails.message);
      if (isNextPageNavAllowed) {
        onNextClick(nextScreen);
      } else {
        SuccessFullSaveObj(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postBankAccountDetails, isNextPageNavAllowed]);

  // Variable which shows the loading state
  const isScreenLoading = React.useMemo(() => {
    return loadingBankAccountDetails || loadingPostBankAccountDetails;
  }, [loadingBankAccountDetails, loadingPostBankAccountDetails]);

  // Hook which is called when there's an Error
  const APIErrorObj = React.useMemo(() => {
    return errortPostBankAccountDetails || errorBankAccountDetails;
  }, [errortPostBankAccountDetails, errorBankAccountDetails]);

  // Hook which is called when the entered Bank Account number is valid or not
  const isEnetredBankAccountValid = (isBankAccNumberValid: boolean) => {
    setIsEneteredBankAccountNumberValid(isBankAccNumberValid);
  };

  // Hook which is called when the entered IFSC number is valid or not
  const isEnteredIFSCValid = (isIfscValid: boolean) => {
    setIsEneteredIFSCValid(isIfscValid);
  };

  // For showing/hiding Bank Account Number
  const showBankAccNumField = React.useMemo(() => {
    return (
      getBankAccountDetails &&
      getBankAccountDetails.fieldsconfig &&
      _.keys(getBankAccountDetails.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getBankAccountDetails.fieldsconfig,
        RegisterationFields.BANK_ACCOUNT_NUMBER
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getBankAccountDetails]);

  // For showing the label OPTIONAL for the Bank Account Number
  const showBankAccNumLabel = React.useMemo(() => {
    return (
      getBankAccountDetails &&
      getBankAccountDetails.fieldsconfig &&
      _.keys(getBankAccountDetails.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getBankAccountDetails.fieldsconfig,
          RegisterationFields.BANK_ACCOUNT_NUMBER
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getBankAccountDetails]);

  // For showing errors [BANK_ACCOUNT_NUMBER]
  const bankAccountDetailsError = React.useMemo(() => {
    return (
      getBankAccountDetails &&
      getBankAccountDetails.errors &&
      getErrorForField(
        RegisterationFields.BANK_ACCOUNT_NUMBER,
        getBankAccountDetails.errors
      )
    );
  }, [getBankAccountDetails]);

  // For showing/hiding IFSC Number
  const showIFSCField = React.useMemo(() => {
    return (
      getBankAccountDetails &&
      getBankAccountDetails.fieldsconfig &&
      _.keys(getBankAccountDetails.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getBankAccountDetails.fieldsconfig,
        RegisterationFields.IFSC_NUMBER
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getBankAccountDetails]);

  // For showing the label OPTIONAL for the IFSC Number
  const showIFSCLabel = React.useMemo(() => {
    return (
      getBankAccountDetails &&
      getBankAccountDetails.fieldsconfig &&
      _.keys(getBankAccountDetails.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getBankAccountDetails.fieldsconfig,
          RegisterationFields.IFSC_NUMBER
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getBankAccountDetails]);

  // For showing errors [IFSC_NUMBER]
  const ifscError = React.useMemo(() => {
    return (
      getBankAccountDetails &&
      getBankAccountDetails.errors &&
      getErrorForField(
        RegisterationFields.IFSC_NUMBER,
        getBankAccountDetails.errors
      )
    );
  }, [getBankAccountDetails]);

  return (
    <span css={[responsiveStyles, bankAccountDetailsStyle]}>
      <ShowMessage
        visible={isScreenLoading}
        loading={isScreenLoading}
        heading={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: loadingBankAccountDetails,
            postLoginScreen: loadingPostBankAccountDetails,
          })?.loadingHeading
        }
        description={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: loadingBankAccountDetails,
            postLoginScreen: loadingPostBankAccountDetails,
          })?.loadingDescription
        }
      />
      <RegistrationLayout>
        <h2>Bank Account Details</h2>
        {/* as account number & account holder name would go together, validating disable/enable on the basis of acc no. field */}
        {showBankAccNumField && (
          <InputField
            placeholder={`Account holder name ${
              showBankAccNumLabel
                ? showBankAccNumLabel
                : inputFieldMandatoryLabel
            }`}
            type="text"
            initialValue={bankAccHolderName}
            onChange={onBankAccHolderNameChange}
          />
        )}

        {showBankAccNumField && (
          <InputField
            placeholder={`Account number ${
              showBankAccNumLabel
                ? showBankAccNumLabel
                : inputFieldMandatoryLabel
            }`}
            type="number"
            initialValue={bankAccNumber}
            onChange={onBankAccNumberChange}
            validationRegEx={regexValidation.bankAccountNumber}
            validate={isEnetredBankAccountValid}
            validationErrMsg={`${
              bankAccountDetailsError
                ? bankAccountDetailsError
                : "Please enter a valid bank account number"
            }`}
          />
        )}

        {showIFSCField && (
          <InputField
            placeholder={`IFSC number ${
              showIFSCLabel ? showIFSCLabel : inputFieldMandatoryLabel
            }`}
            type="text"
            capitaliseInput
            initialValue={ifscCode}
            onChange={onIFSCNumberChange}
            validationRegEx={regexValidation.ifscCode}
            validate={isEnteredIFSCValid}
            validationErrMsg={`${
              ifscError ? ifscError : "Please enter a valid IFSC number"
            } `}
          />
        )}

        {APIErrorObj && <ErrorComponent errorObj={APIErrorObj} />}
        <div
          className={`d-flex align-items-center justify-content-center my-4 p-4 ${
            isMobileView
              ? "internet-connectivity-block-info"
              : "internet-connectivity-block-info-tabmonitor"
          }`}
        >
          <div className="d-flex align-items-start">
            <img src={errorLogo} alt="" className="logo-responsive-styling" />
            <div className="ml-2 display-content-style">
              We’ll check your bank account details for verification
              automatically
            </div>
          </div>
        </div>
      </RegistrationLayout>
      <RegistrationFooterButton
        disableNextButton={isSubmitAllowed}
        onPositiveActionClick={onPositiveActionClick}
        onBackButtonClick={onBackButtonClick}
      />
    </span>
  );
};
